import React, { useState, useCallback } from 'react'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'

interface OpenPDFButtonProps {
    pdfUrl: string
    title?: string
    className?: string
    children?: React.ReactNode
}

/**
 * 修复教材 URL：将 IP 地址替换为域名
 * 管理端使用 talknect.com 域名
 */
function fixMaterialUrl(url: string): string {
    if (!url) return url

    // 管理端使用 talknect.com
    const targetDomain = 'https://talknect.com'

    // 替换 IP 地址为域名
    // http://43.136.166.200/uploads/... -&gt; https://talknect.com/api/v1/files/...
    if (url.includes('43.136.166.200')) {
        // 从路径中提取 material 部分
        const match = url.match(/\/uploads\/(material\/\d+\/.*)/)
        if (match) {
            return `${targetDomain}/api/v1/files/${match[1]}`
        }
        // 其他 uploads 路径
        const uploadsMatch = url.match(/\/uploads\/(.*)/)
        if (uploadsMatch) {
            return `${targetDomain}/api/v1/files/${uploadsMatch[1]}`
        }
    }

    // 如果已经是相对路径 /api/v1/files/...，加上域名
    if (url.startsWith('/api/v1/files/')) {
        return `${targetDomain}${url}`
    }

    return url
}

/**
 * 在新窗口打开 PDF 的按钮组件
 * 通过 API 请求获取 PDF 文件（自动携带 JWT token），然后在新窗口打开
 */
export function OpenPDFButton({ pdfUrl, title, className, children }: OpenPDFButtonProps) {
    const [loading, setLoading] = useState(false)

    const handleOpenPDF = useCallback(async () => {
        if (loading) return

        setLoading(true)

        // ✅ 关键修复：在同步上下文中先打开窗口，避免浏览器弹窗拦截
        const newWindow = window.open('', '_blank')
        if (!newWindow) {
            alert('无法打开新窗口，请允许浏览器弹窗')
            setLoading(false)
            return
        }

        // 显示加载提示
        newWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>加载中...</title>
                <style>
                    body {
                        margin: 0;
                        padding: 0;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        height: 100vh;
                        font-family: system-ui, -apple-system, sans-serif;
                        background: #f3f4f6;
                    }
                    .loader {
                        text-align: center;
                    }
                    .spinner {
                        border: 4px solid #e5e7eb;
                        border-top: 4px solid #9333ea;
                        border-radius: 50%;
                        width: 40px;
                        height: 40px;
                        animation: spin 1s linear infinite;
                        margin: 0 auto 16px;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                </style>
            </head>
            <body>
                <div class="loader">
                    <div class="spinner"></div>
                    <p>正在加载PDF...</p>
                </div>
            </body>
            </html>
        `)

        try {
            // 获取 token
            const token = localStorage.getItem('token')
            if (!token) {
                newWindow.close()
                alert('请先登录')
                setLoading(false)
                return
            }

            // 修复 URL
            const fixedUrl = fixMaterialUrl(pdfUrl)
            console.log('[OpenPDFButton] 原始URL:', pdfUrl)
            console.log('[OpenPDFButton] 修复后URL:', fixedUrl)

            // 通过 API 请求获取 PDF（自动携带认证）
            const response = await fetch(fixedUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })

            if (!response.ok) {
                const data = await response.json().catch(() => ({}))
                newWindow.close()
                alert(data.error?.message || `加载失败 (${response.status})`)
                setLoading(false)
                return
            }

            // 将响应转为 Blob URL
            const blob = await response.blob()
            const blobUrl = URL.createObjectURL(blob)

            // 在已打开的窗口中加载 PDF
            newWindow.location.href = blobUrl

            // 延迟释放 Blob URL（给浏览器足够时间加载）
            setTimeout(() => {
                URL.revokeObjectURL(blobUrl)
            }, 60000) // 1分钟后释放

        } catch (err: any) {
            console.error('Failed to open PDF:', err)
            newWindow.close()
            alert(err.message || '打开失败')
        } finally {
            setLoading(false)
        }
    }, [pdfUrl, loading])

    return (
        <button
            onClick={handleOpenPDF}
            disabled={loading}
            className={className || "font-medium text-purple-600 hover:text-purple-800 underline text-left inline-flex items-center gap-1"}
        >
            {loading ? (
                <span className="inline-flex items-center">
                    <span className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full mr-1"></span>
                    加载中...
                </span>
            ) : (
                <>
                    {children || title || '打开教材'}
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </>
            )}
        </button>
    )
}

export default OpenPDFButton
